import { Grid, Skeleton } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import {
	TermSheetGovernanceTypes,
	TermSheetGovernanceTypesMap,
	TermSheetRevision,
	UpdateTermSheetRevisionDto,
} from '../../../shared/models';
import { FormikUtils, NumberUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';
import { QueryKeys } from '../../../shared/hooks';

interface GovernanceFormProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	revision: TermSheetRevision;
	isLoading: boolean;
}

export const GovernanceForm: React.FC<GovernanceFormProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	revision,
	isLoading,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const governanceMutation = useMutation(
		async ({ values }: GovernanceFormMutationVariables) => {
			const patchValues = {
				termSheetGovernanceType: values.termSheetGovernanceType,
				numberBoardSeats: NumberUtils.ParseDecimal(values.numberBoardSeats, undefined),
				governanceDetails: values.governanceDetails,
				sourcePage: 'Governance',
			} as UpdateTermSheetRevisionDto;

			const { data, error } = await getApiResponseData<void>(
				api.patch(
					CerebroApiEndpoints.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid),
					patchValues
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Term sheet revision was updated.', { variant: 'success' });
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid));
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	if (isLoading) {
		return (
			<>
				<Skeleton height={60} />
				<Skeleton height={60} />
				<Skeleton height={60} />
			</>
		);
	}

	return (
		<Formik
			initialValues={{
				termSheetGovernanceType: revision.termSheetGovernanceType ?? 'NoValue',
				numberBoardSeats: NumberUtils.FormatDecimal(revision.numberBoardSeats),
				governanceDetails: revision.governanceDetails,
			}}
			validate={validateForm}
			onSubmit={FormikUtils.MutateOnSubmit(governanceMutation.mutate)}
		>
			{({ isSubmitting, errors, touched, handleSubmit, values }) => {
				return (
					<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ marginTop: 3, paddingRight: 3 }}>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={11}>
								<CerebroLabel id="termSheetGovernanceType">
									Which governance requirements are required?
								</CerebroLabel>
								<Field
									id="termSheetGovernanceType"
									as={CerebroDropdown}
									name="termSheetGovernanceType"
									disabled={isSubmitting}
								>
									{Object.entries(TermSheetGovernanceTypesMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
								{errors.termSheetGovernanceType && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.termSheetGovernanceType}
									</CerebroText>
								)}
							</Grid>

							{values.termSheetGovernanceType == 'BoardSeats' && (
								<Grid item xs={11}>
									<CerebroLabel id="numberBoardSeats">Board Seat(s)</CerebroLabel>
									<Field
										id="numberBoardSeats"
										as={CerebroMaskedInput}
										name="numberBoardSeats"
										disabled={isSubmitting}
										error={touched.numberBoardSeats && errors.numberBoardSeats !== undefined}
									/>
									{errors.numberBoardSeats && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.numberBoardSeats}
										</CerebroText>
									)}
								</Grid>
							)}
							<Grid item xs={11}>
								<CerebroLabel id="governanceDetails">Governance Details</CerebroLabel>
								<Field
									id="governanceDetails"
									as={CerebroTextarea}
									name="governanceDetails"
									rows={10}
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={8}>
								<CerebroPrimaryButton loading={isSubmitting} type="submit">
									Save
								</CerebroPrimaryButton>
							</Grid>
						</Grid>
					</CerebroForm>
				);
			}}
		</Formik>
	);
};

type GovernanceFormMutationVariables = FormikUtils.MutateOnSubmitVariables<GovernanceFormValues>;
interface GovernanceFormValues {
	termSheetGovernanceType: TermSheetGovernanceTypes;
	numberBoardSeats: string;
	governanceDetails: string;
}
type GovernanceFormValueErrors = FormikErrors<GovernanceFormValues>;

function validateForm(values: GovernanceFormValues) {
	const errors: GovernanceFormValueErrors = {};

	if (!values.termSheetGovernanceType || values.termSheetGovernanceType == 'NoValue') {
		errors.termSheetGovernanceType = 'Governance requirement is required.';
	}

	if (values.termSheetGovernanceType == 'BoardSeats') {
		if (!values.numberBoardSeats || values.numberBoardSeats === '') {
			errors.numberBoardSeats = 'You enter a valid number of board seats.';
		}
	}

	return errors;
}
